import React, { createContext, useCallback, useContext, useEffect, useId, useMemo, useRef, useState, SyntheticEvent } from 'react';
import Dropdown from 'react-bootstrap/Dropdown';
import { useTranslation } from 'react-i18next';
import { Icon } from '@fikasio/react-ui-components';
import CancellationReasonModal from 'components/UI/CancellationReasonModal';
import { Task } from 'services/tasks/types';
import styles from './TasksList.module.css';

export const OpenStatusDropdownContext = createContext<{
  openDropdownId: string | null;
  setOpenDropdownId: (id: string | null) => void;
} | null>(null);

export const OpenStatusDropdownProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [openDropdownId, setOpenDropdownId] = useState<string | null>(null);
  const value = useMemo(() => ({
    openDropdownId,
    setOpenDropdownId,
  }), [openDropdownId]);

  return (
    <OpenStatusDropdownContext.Provider value={value}>
      {children}
    </OpenStatusDropdownContext.Provider>
  );
};

type TaskStatus = NonNullable<Task['status']>;

interface TaskStatusOption {
  color: string;
  icon: 'archive' | 'caret-right' | 'check' | 'square' | 'times' | 'triangle-exclamation';
  labelKey: 'archive' | 'blocked' | 'cancelled' | 'completed' | 'deleted' | 'doing' | 'failed' | 'notDone' | 'status' | 'todo';
  value: string;
}

export const TASK_STATUS_OPTIONS: Array<TaskStatusOption & { value: Exclude<TaskStatus, 'Deleted'> | 'Archived' }> = [
  { color: '#607d8b', icon: 'square', labelKey: 'todo', value: 'Todo' },
  { color: '#f5a623', icon: 'caret-right', labelKey: 'doing', value: 'Doing' },
  { color: '#f44336', icon: 'triangle-exclamation', labelKey: 'blocked', value: 'Blocked' },
  { color: '#4caf50', icon: 'check', labelKey: 'completed', value: 'Completed' },
  { color: '#c62828', icon: 'times', labelKey: 'notDone', value: 'NotDone' },
  { color: '#9e9e9e', icon: 'times', labelKey: 'cancelled', value: 'Cancelled' },
  { color: '#5c6bc0', icon: 'archive', labelKey: 'archive', value: 'Archived' },
];

const DELETED_STATUS: TaskStatusOption & { value: 'Deleted' } = {
  color: '#9e9e9e',
  icon: 'times',
  labelKey: 'deleted',
  value: 'Deleted',
};

const EMPTY_STATUS: TaskStatusOption = {
  color: '#cecece',
  icon: 'square',
  labelKey: 'status',
  value: '',
};

export const getTaskStatusOption = (status?: string) => (
  [...TASK_STATUS_OPTIONS, DELETED_STATUS].find(option => option.value === status) ?? EMPTY_STATUS
);

interface TaskStatusToggleProps {
  'aria-expanded'?: boolean;
  className?: string;
  onClick?: (event: SyntheticEvent) => void;
  status?: string;
  statusOptions?: readonly TaskStatusOption[];
}

const TaskStatusToggle = React.forwardRef<HTMLDivElement, TaskStatusToggleProps>(({
  'aria-expanded': ariaExpanded,
  className,
  onClick,
  status,
  statusOptions,
  ...props
}, ref) => {
  const { t } = useTranslation();
  const option = statusOptions?.find(item => item.value === status) ?? getTaskStatusOption(status);

  return (
    <div
      {...props}
      aria-expanded={ariaExpanded}
      aria-haspopup="menu"
      aria-label={t(option.labelKey)}
      className={`${styles.taskRowStatusToggle}${className ? ` ${className}` : ''}`}
      onClick={event => {
        event.preventDefault();
        event.stopPropagation();
        onClick?.(event);
      }}
      ref={ref}
      role="button"
      style={{ color: option.color }}
      tabIndex={0}
      title={t(option.labelKey)}
    >
      <Icon
        name={option.icon}
        size="1x"
      />
    </div>
  );
});

TaskStatusToggle.displayName = 'TaskStatusToggle';

interface TaskStatusDropdownProps<T extends string = Exclude<TaskStatus, 'Deleted'> | 'Archived'> {
  alignToggle?: 'center' | 'start';
  onChange: (
    status: T,
    extras?: { cancelReason?: string | null },
  ) => void;
  status?: string;
  statusOptions?: ReadonlyArray<TaskStatusOption & { value: T }>;
}

const TaskStatusDropdown = <T extends string = Exclude<TaskStatus, 'Deleted'> | 'Archived'>({
  alignToggle = 'center',
  onChange,
  status,
  statusOptions,
}: TaskStatusDropdownProps<T>) => {
  const { t } = useTranslation();
  const dropdownId = useId();
  const rootRef = useRef<HTMLDivElement>(null);
  const exclusiveDropdown = useContext(OpenStatusDropdownContext);
  const [internalOpen, setInternalOpen] = useState(false);
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const options = statusOptions ?? TASK_STATUS_OPTIONS;
  const isOpen = exclusiveDropdown
    ? exclusiveDropdown.openDropdownId === dropdownId
    : internalOpen;

  const setOpen = useCallback((nextShow: boolean) => {
    if (exclusiveDropdown) {
      exclusiveDropdown.setOpenDropdownId(nextShow ? dropdownId : null);
      return;
    }
    setInternalOpen(nextShow);
  }, [dropdownId, exclusiveDropdown]);

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    // Capture so a click still closes the menu when the target stops bubbling,
    // which row selection and inline fields do.
    const handleClick = (event: MouseEvent) => {
      const target = event.target;
      if (target instanceof Node && rootRef.current?.contains(target)) {
        return;
      }
      setOpen(false);
    };

    document.addEventListener('click', handleClick, true);
    return () => document.removeEventListener('click', handleClick, true);
  }, [isOpen, setOpen]);

  const selectStatus = (nextStatus: T) => {
    setOpen(false);
    if (nextStatus === 'Cancelled' && status !== 'Cancelled') {
      setIsCancelModalOpen(true);
      return;
    }
    onChange(nextStatus);
  };

  return (
    <>
      <Dropdown
        onToggle={nextShow => setOpen(nextShow)}
        ref={rootRef}
        show={isOpen}
      >
        <Dropdown.Toggle
          as={TaskStatusToggle as React.ElementType}
          className={alignToggle === 'start' ? styles.taskStatusToggleStart : undefined}
          status={status}
          statusOptions={options}
        />
        <Dropdown.Menu
          data-testid="task-status-menu"
          popperConfig={{
            modifiers: [{ name: 'hide', enabled: false }],
          }}
        >
          {
            options.map(option => (
              <Dropdown.Item
                active={status === option.value}
                key={option.value}
                onClick={() => selectStatus(option.value as T)}
              >
                <Icon
                  name={option.icon}
                  style={{
                    color: option.color,
                    marginRight: 10,
                    width: 18,
                  }}
                />
                {t(option.labelKey)}
              </Dropdown.Item>
            ))
          }
        </Dropdown.Menu>
      </Dropdown>
      <CancellationReasonModal
        onConfirm={reason => {
          setIsCancelModalOpen(false);
          onChange('Cancelled' as T, { cancelReason: reason || null });
        }}
        onHide={() => setIsCancelModalOpen(false)}
        show={isCancelModalOpen}
      />
    </>
  );
};

export default TaskStatusDropdown;
